import React, { useEffect, useState, useRef } from "react";
import secureAxios from "../../../utils/secureAxios";
import { useAuth } from "../../../contexts/AuthContext";
import WidgetCard, { WidgetMetricBox, WidgetProgressBar } from "./WidgetCard";

const JobLogSummaryWidget = ({ onlyMyData = false }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const hasFetchedRef = useRef(false);
  const lastParamsRef = useRef(null);

  useEffect(() => {
    const fetchJobLogSummary = async () => {
      if (!user?.user_id || !user?.tenant_id) {
        setLoading(false);
        return;
      }

      const paramsKey = `${user.user_id}-${user.tenant_id}-${onlyMyData}`;

      if (hasFetchedRef.current && lastParamsRef.current === paramsKey) {
        return;
      }

      hasFetchedRef.current = true;
      lastParamsRef.current = paramsKey;
      setLoading(true);

      try {
        const response = await secureAxios.get("/api/widgets/job-log-summary", {
          params: {
            only_my_data: onlyMyData,
            user_id: onlyMyData ? user.user_id : undefined,
            tenant_id: user.tenant_id,
          },
        });

        setData(response.data);
        setError(false);
      } catch (error) {
        console.error("Error fetching job log summary:", error);
        setError(true);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchJobLogSummary();
  }, [user?.user_id, user?.tenant_id, onlyMyData]);

  const formatHours = (value) => {
    const num = parseFloat(value || 0);
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toFixed(1);
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)}k`;
    }
    return `$${num.toFixed(0)}`;
  };

  // Calculate derived values
  const calculateTotals = () => {
    if (!data) return null;

    const totalPaidAmount =
      parseFloat(data.this_month_paid_amount || 0) +
      parseFloat(data.last_month_paid_amount || 0) +
      parseFloat(data.this_year_paid_amount || 0);
    const totalUnpaidAmount =
      parseFloat(data.this_month_unpaid_amount || 0) +
      parseFloat(data.last_month_unpaid_amount || 0) +
      parseFloat(data.this_year_unpaid_amount || 0);
    const totalAmount = totalPaidAmount + totalUnpaidAmount;

    const totalPaidHours =
      parseFloat(data.this_month_paid_hours || 0) +
      parseFloat(data.last_month_paid_hours || 0) +
      parseFloat(data.this_year_paid_hours || 0);
    const totalUnpaidHours =
      parseFloat(data.this_month_unpaid_hours || 0) +
      parseFloat(data.last_month_unpaid_hours || 0) +
      parseFloat(data.this_year_unpaid_hours || 0);
    const totalHours = totalPaidHours + totalUnpaidHours;

    const avgRate = totalHours > 0 ? totalAmount / totalHours : 0;

    return {
      totalPaidAmount,
      totalUnpaidAmount,
      totalAmount,
      totalHours,
      avgRate,
    };
  };

  const getPeriods = () => {
    if (!data) return [];

    const currentMonth = new Date().toLocaleString("default", {
      month: "long",
    });
    const lastMonth = new Date(
      new Date().setMonth(new Date().getMonth() - 1)
    ).toLocaleString("default", { month: "long" });
    const currentYear = new Date().getFullYear();

    return [
      {
        name: currentMonth,
        paidHours: parseFloat(data.this_month_paid_hours || 0),
        paidAmount: parseFloat(data.this_month_paid_amount || 0),
        unpaidHours: parseFloat(data.this_month_unpaid_hours || 0),
        unpaidAmount: parseFloat(data.this_month_unpaid_amount || 0),
      },
      {
        name: lastMonth,
        paidHours: parseFloat(data.last_month_paid_hours || 0),
        paidAmount: parseFloat(data.last_month_paid_amount || 0),
        unpaidHours: parseFloat(data.last_month_unpaid_hours || 0),
        unpaidAmount: parseFloat(data.last_month_unpaid_amount || 0),
      },
      {
        name: `${currentYear} YTD`,
        paidHours: parseFloat(data.this_year_paid_hours || 0),
        paidAmount: parseFloat(data.this_year_paid_amount || 0),
        unpaidHours: parseFloat(data.this_year_unpaid_hours || 0),
        unpaidAmount: parseFloat(data.this_year_unpaid_amount || 0),
      },
    ];
  };

  const totals = calculateTotals();
  const periods = getPeriods();

  const getSubtitle = () => {
    const scope = onlyMyData
      ? "Personal"
      : user?.is_admin
      ? "All Employees"
      : "Team";
    const rate = totals ? `$${totals.avgRate.toFixed(0)}/hr avg` : "";
    return `${scope} • ${rate}`;
  };

  const headerRight = user?.is_admin ? (
    <small className="text-muted">
      <i className={`bi bi-${onlyMyData ? "person" : "people"}-fill`}></i>
    </small>
  ) : null;

  return (
    <WidgetCard
      title="Job Log Summary"
      subtitle={!loading && !error && data ? getSubtitle() : undefined}
      icon="bi-briefcase"
      iconColor="primary"
      loading={loading}
      error={error}
      errorMessage="Unable to load data"
      empty={!data}
      emptyMessage="No job log data available"
      headerRight={headerRight}
    >
      {totals && (
        <>
          {/* Key Metrics */}
          <div className="row g-2 mb-3">
            <div className="col-4">
              <WidgetMetricBox
                label="Total Hours"
                value={formatHours(totals.totalHours)}
                color="light"
                textColor="dark"
              />
            </div>
            <div className="col-4">
              <WidgetMetricBox
                label="Paid"
                value={formatCurrency(totals.totalPaidAmount)}
                color="success"
                textColor="success"
              />
            </div>
            <div className="col-4">
              <WidgetMetricBox
                label="Unpaid"
                value={formatCurrency(totals.totalUnpaidAmount)}
                color="warning"
                textColor="warning"
              />
            </div>
          </div>

          {/* Period Details */}
          <div className="mb-3">
            {periods.map((period, index) => (
              <div
                key={period.name}
                className={`widget-period-card ${
                  index < periods.length - 1 ? "mb-2" : ""
                }`}
              >
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-medium small">{period.name}</span>
                  <div className="d-flex gap-2">
                    {period.paidAmount > 0 && (
                      <span className="widget-stat-pill bg-success bg-opacity-10 text-success">
                        {formatHours(period.paidHours)} hrs •{" "}
                        {formatCurrency(period.paidAmount)}
                      </span>
                    )}
                    {period.unpaidAmount > 0 && (
                      <span className="widget-stat-pill bg-warning bg-opacity-10 text-warning">
                        {formatHours(period.unpaidHours)} hrs •{" "}
                        {formatCurrency(period.unpaidAmount)}
                      </span>
                    )}
                  </div>
                </div>
                <WidgetProgressBar
                  segments={[
                    {
                      color: "success",
                      percent:
                        period.paidAmount > 0
                          ? (period.paidAmount /
                              (period.paidAmount + period.unpaidAmount)) *
                            100
                          : 0,
                    },
                    {
                      color: "warning",
                      percent:
                        period.unpaidAmount > 0
                          ? (period.unpaidAmount /
                              (period.paidAmount + period.unpaidAmount)) *
                            100
                          : 0,
                    },
                  ]}
                />
              </div>
            ))}
          </div>

          {/* Overall Progress */}
          <div className="widget-footer">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="text-muted">Payment Progress</small>
              <small className="fw-medium">
                {totals.totalAmount > 0
                  ? Math.round(
                      (totals.totalPaidAmount / totals.totalAmount) * 100
                    )
                  : 0}
                % paid
              </small>
            </div>
            <WidgetProgressBar
              segments={[
                {
                  color: "success",
                  percent:
                    totals.totalAmount > 0
                      ? (totals.totalPaidAmount / totals.totalAmount) * 100
                      : 0,
                },
              ]}
            />
          </div>
        </>
      )}
    </WidgetCard>
  );
};

export default JobLogSummaryWidget;
